import { GAMMA_MEDIUM_CHAIN } from "./gamma";
import { summariseDailyQuality } from "./hourly-windows";
/**
 * SUP (Stand Up Paddleboarding) Scoring V5
 *
 * Purely multiplicative quality-experience score.
 *   hourly = BASE * F_wind * F_wave * F_current * F_comfort
 *                  * F_visibility * F_tide * F_waterQuality * B_golden
 *
 * Design principles:
 *  - Score measures EXPERIENCE QUALITY, never safety. A high score on a
 *    dangerous day means "conditions feel amazing if you can handle them."
 *    Safety is a separate, independent output displayed alongside the score.
 *  - No hard caps. Extreme inputs produce very low factors through the
 *    natural shape of the curves, not through if-then overrides.
 *  - UV removed from quality (sunscreen exists; UV is a health indicator).
 *  - Temperature excluded from score (locals dress for local climate;
 *    including it creates permanent geographic bias). Surfaced via
 *    safety indicators and gear recommendations instead.
 *  - Current quality assumes a return outing and prices the harder return leg;
 *    extreme-current danger is handled by the safety system.
 *  - Wind and waves dominate. SUP balance is acutely sensitive to both.
 */

import type { SunTimes } from "./daylight";
import { isDark } from "./daylight";
import { composeScore, defineChain } from "./compose-score";
import { assessSupOffshoreDrift } from "./sup-offshore-drift";
import { classifySuppliedCurrent } from "./seaward-current";
import { classifyModelFog } from "./model-fog";

// SUP's chain, declared as data (tracker P3-19); classes declared, not enforced (Phase 7).
// Gamma derived from the 7 declared factors (MEDIUM) under the Phase 6
// all-or-none policy (tracker P6-1, audit 90.3). The BASE_SCORE 10.0 scale
// constant is applied outside composeScore so gamma compresses the 0-1 quality
// product, matching every other chain.
export const SUP_CHAIN = defineChain({
  sport: "sup",
  gamma: GAMMA_MEDIUM_CHAIN, // Pinned calibration; factor-list refactors must not change the scale.
  factors: [
    { key: "wind", class: "driver" },
    { key: "wave", class: "driver" },
    { key: "current", class: "driver", note: "effort split from drift flags (07 B-current)" },
    { key: "comfort", class: "driver", note: "Rain intensity affects the paddling experience; light rain remains a small penalty." },
    { key: "visibility", class: "driver", note: "Actual loss of scenery and shore references degrades paddling quality independently of warnings." },
    { key: "tide", class: "driver", note: "access curve, tier-migrated quality (07 B-tide NO CHANGE): a springs-low launch genuinely degrades the session, so it is not a floored enjoyment modifier (P7-1)" },
    { key: "waterQuality", class: "modifier", note: "secchi clarity, floored 0.85, compliant (07 B-secchi)" },
    { key: "golden", class: "structural", note: "bounded golden-hour bonus (07 B-golden)" },
  ],
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ShelterLevel =
  | "exposed"
  | "semi_exposed"
  | "semi_sheltered"
  | "sheltered"
  | "inland";

export type SupHourlyInput = {
  significantWaveHeight: number;
  currentVelocity: number;
  windSpeed: number;
  windGust: number;
  apparentTemp: number;
  precipitation: number;
  precipitationProbability: number;
  cloudCover: number;
  visibility: number;
  uvIndex: number;
  tidePosition: number | null;
  hour: number;
  sunrise: number;
  sunset: number;
  secchiDepth: number | null;
  shelterLevel: ShelterLevel | null;
  breakType: string | null;
  difficulty: number | null;
  isLake: boolean;
  weatherCode?: number;
  capeJkg?: number;
  swellDirectionDeg?: number | null;
  exposedSectors?: [number, number][] | null;
  beachFacingDeg?: number | null;
  exposedCeiling?: number | null;
  shelteredFloor?: number | null;
};

export type SupFactors = {
  wind: number;
  wave: number;
  current: number;
  comfort: number;
  visibility: number;
  tide: number;
  waterQuality: number;
  golden: number;
};

export type SafetySeverity = "green" | "amber" | "red";

export type SafetyIndicator = {
  key: string;
  severity: SafetySeverity | "unavailable";
  label: string;
  detail: string;
  confidence?: "full" | "unavailable";
  provenance?: "missing-forecast-input";
};

export type SupSafetyResult = {
  overall: SafetySeverity;
  indicators: SafetyIndicator[];
};

export type SupHourlyResult = {
  score: number;
  label: string;
  factors: SupFactors;
  safety: SupSafetyResult;
  effectiveWaveHeight: number;
  safetyCapped: boolean;
  safetyCapReasons: string[];
};

// ---------------------------------------------------------------------------
// Constants - every threshold, weight, and floor is named here
// ---------------------------------------------------------------------------

const BASE_SCORE = 10.0;

// Wind: quadratic decay. SUP balance is destroyed by any wind direction.
const WIND_BLEND_SUSTAINED = 0.7;
const WIND_BLEND_GUST = 0.3;
const WIND_THRESHOLD_KMH = 30;
const WIND_FLOOR = 0.05;

// Waves: quadratic decay. Flat water is the goal; SUP balance is very
// sensitive to chop. Threshold tighter than surf/swim because a standing
// paddler is far less stable.
const WAVE_THRESHOLD_M = 1.2;
const WAVE_FLOOR = 0.05;

// Touring assumes a return to the starting point, not a current-assisted drift.
// Preserve the gentle-current response, then price the increasingly difficult
// return leg. These are experience anchors, not a claimed personal paddle speed.
// The safety system handles drift risk independently.
const GENTLE_CURRENT_MAX_MS = 0.5;
const GENTLE_CURRENT_SCALE_MS = 1.5;
const RETURN_CURRENT_CURVE: [number, number][] = [
  [GENTLE_CURRENT_MAX_MS, 1 - (GENTLE_CURRENT_MAX_MS / GENTLE_CURRENT_SCALE_MS) ** 2],
  [0.75, 0.60],
  [1.0, 0.25],
  [1.25, 0.08],
  [1.5, 0.02],
  [2.0, 0.005],
];

// Temperature is deliberately NOT scored (see design principles in the file
// header). The comfort factor covers precipitation only; apparentTemp on the
// input is retained for safety indicators and gear recommendations downstream.

// Comfort - precipitation. Light mist (< 0.2 mm/h) can be atmospheric
// on flat water; above that it degrades the experience.
const PRECIP_BREAKPOINTS: [number, number][] = [
  [0.0, 1.0],
  [0.2, 1.0],
  [0.5, 0.93],
  [1.0, 0.85],
  [3.0, 0.65],
  [5.0, 0.50],
  [10.0, 0.35],
  [15.0, 0.30],
];
const PRECIP_PROB_THRESHOLD_PCT = 60;
const PRECIP_PROB_NUDGE_PER_PCT = 0.002;

// Visibility - harsher at low values because SUP paddlers navigate
// visually and cannot see hazards, shore, or boat traffic in fog.
const VIS_BREAKPOINTS: [number, number][] = [
  [100, 0.15],
  [300, 0.30],
  [500, 0.45],
  [1000, 0.65],
  [2000, 0.80],
  [5000, 0.93],
  [10000, 1.0],
];

// Cloud cover - overcast is a mild aesthetic penalty, not a real problem.
const CLOUD_BREAKPOINTS: [number, number][] = [
  [0, 1.0],
  [60, 1.0],
  [85, 0.96],
  [100, 0.92],
];

// Tide access - beach launch difficulty at extremes of the tidal range.
const TIDE_ACCESS_BREAKPOINTS: [number, number][] = [
  [0.0, 0.80],
  [0.1, 0.80],
  [0.25, 0.92],
  [0.3, 1.0],
  [0.7, 1.0],
  [0.75, 1.0],
  [0.9, 0.95],
  [1.0, 0.85],
];
const TIDE_DEFAULT_FACTOR = 0.95;

// Tide state inferred from normalised position.
const TIDE_SLACK_THRESHOLD = 0.1;
const TIDE_PEAK_LOW = 0.35;
const TIDE_PEAK_HIGH = 0.65;
const TIDE_STATE_SLACK = 1.0;
const TIDE_STATE_MID = 0.95;
const TIDE_STATE_PEAK = 0.90;

// Water quality (Secchi transparency depth). Defaults to 1.0 when
// Copernicus data is unavailable. Mild bonus for exceptionally clear water.
const WATER_QUALITY_BREAKPOINTS: [number, number][] = [
  [0.0, 0.85],
  [0.5, 0.85],
  [1.0, 0.90],
  [2.0, 0.95],
  [4.0, 1.0],
  [8.0, 1.0],
  [12.0, 1.02],
];

// Golden hour bonus - dawn/dusk on glassy water is quintessential SUP.
// Gated: only applies when wind AND waves are below thresholds.
const GOLDEN_WIND_GATE_KMH = 10;
const GOLDEN_WAVE_GATE_M = 0.3;
const GOLDEN_BREAKPOINTS: [number, number][] = [
  [0.0, 1.05],
  [0.5, 1.05],
  [1.0, 1.02],
  [2.0, 1.0],
];

// Shelter factors - open-ocean wave models overestimate wave height
// in harbours, bays, and estuaries.
const SHELTER_FACTORS: Record<ShelterLevel, number> = {
  exposed: 1.0,
  semi_exposed: 0.85,
  semi_sheltered: 0.6,
  sheltered: 0.35,
  inland: 1.0,
};

const DIRECTIONAL_RANGES: Record<ShelterLevel, { ceiling: number; floor: number }> = {
  exposed: { ceiling: 1.0, floor: 1.0 },
  semi_exposed: { ceiling: 1.0, floor: 0.70 },
  semi_sheltered: { ceiling: 0.85, floor: 0.40 },
  sheltered: { ceiling: 0.55, floor: 0.15 },
  inland: { ceiling: 1.0, floor: 1.0 },
};

const TRANSITION_DEG = 30;

// Safety indicator thresholds (separate from quality score).
const SAFETY_CURRENT_AMBER_MS = 0.5;
const SAFETY_CURRENT_RED_MS = 1.0;
const SAFETY_GUST_AMBER_KMH = 30;
const SAFETY_GUST_RED_KMH = 40;
const SAFETY_WAVE_AMBER_M = 1.5;
const SAFETY_WAVE_RED_M = 2.0;
const SAFETY_VIS_AMBER_M = 1000;
const SAFETY_VIS_RED_M = 300;
const SAFETY_UV_AMBER = 6;
const SAFETY_UV_RED = 8;
const SAFETY_CAPE_RED = 1000;
const THUNDERSTORM_CODES = new Set([95, 96, 99]);
const CONVECTIVE_RAIN_CODES = new Set([61, 63, 65, 80, 81, 82]);
const SAFETY_CAPE_AMBER = 500;

// Daily derivation weights.
const DAILY_BEST_WINDOW_WEIGHT = 0.7;
const BEST_WINDOW_HOURS = 3;

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

function quadratic(value: number, threshold: number, floor: number): number {
  return Math.max(floor, 1 - Math.pow(value / threshold, 2));
}

function piecewise(value: number, breakpoints: [number, number][]): number {
  if (value <= breakpoints[0][0]) return breakpoints[0][1];
  const last = breakpoints[breakpoints.length - 1];
  if (value >= last[0]) return last[1];
  for (let i = 0; i < breakpoints.length - 1; i++) {
    const [x0, y0] = breakpoints[i];
    const [x1, y1] = breakpoints[i + 1];
    if (value >= x0 && value <= x1) {
      const t = (value - x0) / (x1 - x0);
      return y0 + t * (y1 - y0);
    }
  }
  return last[1];
}

function clampScore(raw: number): number {
  return Math.round(Math.min(10.0, Math.max(0.0, raw)) * 10) / 10;
}

// ---------------------------------------------------------------------------
// Shelter factor
// ---------------------------------------------------------------------------

function normaliseBreakType(raw: string | null): string | null {
  if (!raw) return null;
  const lower = raw.toLowerCase().replace(/\s+/g, "_");
  if (lower === "point" || lower === "point_break") return "point_break";
  if (lower === "reef" || lower === "reef_break") return "reef_break";
  if (lower === "beach" || lower === "beach_break") return "beach_break";
  return lower;
}

function isInSector(deg: number, sector: [number, number]): boolean {
  const [start, end] = sector;
  if (start <= end) return deg >= start && deg <= end;
  return deg >= start || deg <= end;
}

function angularDistanceToSectors(deg: number, sectors: [number, number][]): number {
  let minDist = 180;
  for (const sector of sectors) {
    if (isInSector(deg, sector)) return 0;
    const distStart = Math.min(
      Math.abs(deg - sector[0]),
      360 - Math.abs(deg - sector[0])
    );
    const distEnd = Math.min(
      Math.abs(deg - sector[1]),
      360 - Math.abs(deg - sector[1])
    );
    minDist = Math.min(minDist, distStart, distEnd);
  }
  return minDist;
}

function resolveExposedSectors(
  exposedSectors: [number, number][] | null,
  beachFacingDeg: number | null
): [number, number][] | null {
  if (exposedSectors && exposedSectors.length > 0) return exposedSectors;
  if (beachFacingDeg !== null) {
    const start = (beachFacingDeg - 90 + 360) % 360;
    const end = (beachFacingDeg + 90) % 360;
    return [[start, end]];
  }
  return null;
}

function directionalModulation(
  ceiling: number,
  floor: number,
  swellDeg: number,
  sectors: [number, number][]
): number {
  const dist = angularDistanceToSectors(swellDeg, sectors);
  if (dist === 0) return ceiling;
  if (dist >= TRANSITION_DEG) return floor;
  const t = dist / TRANSITION_DEG;
  const smooth = 0.5 * (1 - Math.cos(Math.PI * t));
  return ceiling + smooth * (floor - ceiling);
}

export type DirectionalParams = {
  swellDirectionDeg: number | null;
  exposedSectors: [number, number][] | null;
  beachFacingDeg: number | null;
  exposedCeiling?: number | null;
  shelteredFloor?: number | null;
};

function inferFromBreakType(breakType: string | null, difficulty: number | null): number {
  const bt = normaliseBreakType(breakType);
  const diff = difficulty ?? 3;
  if (bt === "point_break") return SHELTER_FACTORS.semi_exposed;
  if (bt === "reef_break")
    return diff <= 2
      ? SHELTER_FACTORS.semi_sheltered
      : SHELTER_FACTORS.semi_exposed;
  if (bt === "beach_break")
    return diff <= 1
      ? SHELTER_FACTORS.semi_sheltered
      : SHELTER_FACTORS.exposed;
  return SHELTER_FACTORS.exposed;
}

function inferShelterLevel(breakType: string | null, difficulty: number | null): ShelterLevel {
  const bt = normaliseBreakType(breakType);
  const diff = difficulty ?? 3;
  if (bt === "point_break") return "semi_exposed";
  if (bt === "reef_break") return diff <= 2 ? "semi_sheltered" : "semi_exposed";
  if (bt === "beach_break") return diff <= 1 ? "semi_sheltered" : "exposed";
  return "exposed";
}

export function getShelterFactor(
  shelterLevel: ShelterLevel | null,
  breakType: string | null,
  difficulty: number | null,
  directional?: DirectionalParams
): number {
  const baseFactor = shelterLevel
    ? SHELTER_FACTORS[shelterLevel]
    : inferFromBreakType(breakType, difficulty);

  if (!directional || directional.swellDirectionDeg === null) return baseFactor;

  const resolvedLevel = shelterLevel ?? inferShelterLevel(breakType, difficulty);
  const range = DIRECTIONAL_RANGES[resolvedLevel];
  const ceiling = directional.exposedCeiling ?? range.ceiling;
  const floor = directional.shelteredFloor ?? range.floor;

  if (ceiling === floor) return baseFactor;

  const sectors = resolveExposedSectors(
    directional.exposedSectors,
    directional.beachFacingDeg
  );
  if (!sectors) return baseFactor;

  return directionalModulation(ceiling, floor, directional.swellDirectionDeg, sectors);
}

// ---------------------------------------------------------------------------
// Factor functions
// ---------------------------------------------------------------------------

function effectiveWind(input: SupHourlyInput): number {
  return input.windGust > 0
    ? input.windSpeed * WIND_BLEND_SUSTAINED + input.windGust * WIND_BLEND_GUST
    : input.windSpeed;
}

export function supWindFactor(input: SupHourlyInput): number {
  return quadratic(effectiveWind(input), WIND_THRESHOLD_KMH, WIND_FLOOR);
}

export function supWaveFactor(ewh: number): number {
  return quadratic(ewh, WAVE_THRESHOLD_M, WAVE_FLOOR);
}

export function supCurrentFactor(velocity: number): number {
  if (velocity <= GENTLE_CURRENT_MAX_MS) {
    return quadratic(Math.max(0, velocity), GENTLE_CURRENT_SCALE_MS, 0);
  }
  return piecewise(velocity, RETURN_CURRENT_CURVE);
}

// The SUP comfort factor is precipitation-only (it deliberately excludes air
// temperature for geographic neutrality). Exposed standalone so the radar can
// reuse the exact model curve rather than re-deriving an air-temp comfort.
export function supComfortFromPrecip(
  precipitation: number,
  precipitationProbability: number
): number {
  if (precipitation > 0) {
    return piecewise(precipitation, PRECIP_BREAKPOINTS);
  }
  if (precipitationProbability > PRECIP_PROB_THRESHOLD_PCT) {
    return (
      1.0 -
      (precipitationProbability - PRECIP_PROB_THRESHOLD_PCT) *
        PRECIP_PROB_NUDGE_PER_PCT
    );
  }
  return 1.0;
}

export function supComfortFactor(input: SupHourlyInput): number {
  return supComfortFromPrecip(input.precipitation, input.precipitationProbability);
}

export function supAtmosphericVisibilityFactor(visibilityM: number): number {
  return piecewise(visibilityM, VIS_BREAKPOINTS);
}

export function supCloudFactor(cloudCoverPct: number): number {
  return piecewise(cloudCoverPct, CLOUD_BREAKPOINTS);
}

export function supVisibilityFactor(input: SupHourlyInput): number {
  return (
    supAtmosphericVisibilityFactor(input.visibility) *
    supCloudFactor(input.cloudCover)
  );
}

// Tide factor from a normalised tide position alone (0 = low, 1 = high),
// exposed so the radar can reuse the exact model curve without a full input.
export function supTideFromPosition(tidePosition: number | null): number {
  if (tidePosition === null) return TIDE_DEFAULT_FACTOR;

  const tAccess = piecewise(tidePosition, TIDE_ACCESS_BREAKPOINTS);
  const tideState = inferTideState(tidePosition);
  const tState =
    tideState === "slack"
      ? TIDE_STATE_SLACK
      : tideState === "mid-flow"
        ? TIDE_STATE_MID
        : TIDE_STATE_PEAK;

  return tAccess * tState;
}

export function supTideFactor(input: SupHourlyInput): number {
  if (input.isLake) return 1.0;
  return supTideFromPosition(input.tidePosition);
}

export function supWaterQualityFactor(secchiDepth: number | null): number {
  if (secchiDepth === undefined || secchiDepth === null) return 1.0;
  return piecewise(secchiDepth, WATER_QUALITY_BREAKPOINTS);
}

export function supGoldenBonus(input: SupHourlyInput, ewh: number): number {
  if (effectiveWind(input) >= GOLDEN_WIND_GATE_KMH || ewh >= GOLDEN_WAVE_GATE_M)
    return 1.0;

  const offsetFromSunrise = Math.abs(input.hour - input.sunrise);
  const offsetFromSunset = Math.abs(input.hour - input.sunset);
  const offset = Math.min(offsetFromSunrise, offsetFromSunset);

  return piecewise(offset, GOLDEN_BREAKPOINTS);
}

function inferTideState(
  tidePosition: number
): "slack" | "mid-flow" | "peak-flow" {
  if (tidePosition < TIDE_SLACK_THRESHOLD || tidePosition > 1 - TIDE_SLACK_THRESHOLD)
    return "slack";
  if (tidePosition > TIDE_PEAK_LOW && tidePosition < TIDE_PEAK_HIGH)
    return "peak-flow";
  return "mid-flow";
}

// ---------------------------------------------------------------------------
// Safety indicators - independent of quality score
// ---------------------------------------------------------------------------

function worstSeverity(a: SafetySeverity, b: SafetySeverity): SafetySeverity {
  const rank: Record<SafetySeverity, number> = { green: 0, amber: 1, red: 2 };
  return rank[a] >= rank[b] ? a : b;
}

export function computeSupSafety(input: {
  currentVelocity: number | null;
  windGust: number | null;
  effectiveWaveHeight: number | null;
  visibility: number | null;
  uvIndex: number;
  weatherCode?: number;
  capeJkg?: number;
}): SupSafetyResult {
  const indicators: SafetyIndicator[] = [];

  if (input.currentVelocity === null) {
    indicators.push({ key: "current_unavailable", severity: "unavailable", label: "Current data unavailable", detail: "The current forecast is missing, so return-journey drift cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.currentVelocity > SAFETY_CURRENT_RED_MS) {
    indicators.push({
      key: "current_extreme",
      severity: "red",
      label: "Strong current",
      detail: `Current ${input.currentVelocity.toFixed(1)} m/s exceeds SUP cruising speed. Risk of being swept offshore.`,
    });
  } else if (input.currentVelocity > SAFETY_CURRENT_AMBER_MS) {
    indicators.push({
      key: "current_moderate",
      severity: "amber",
      label: "Moderate current",
      detail: `Current ${input.currentVelocity.toFixed(1)} m/s. Be aware of drift and plan return route.`,
    });
  }

  if (input.windGust === null) {
    indicators.push({ key: "gust_unavailable", severity: "unavailable", label: "Gust data unavailable", detail: "The gust forecast is missing, so knockdown risk cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.windGust > SAFETY_GUST_RED_KMH) {
    indicators.push({
      key: "gust_extreme",
      severity: "red",
      label: "Dangerous gusts",
      detail: `Gusts ${Math.round(input.windGust)} km/h. Risk of knockdown and being blown offshore.`,
    });
  } else if (input.windGust > SAFETY_GUST_AMBER_KMH) {
    indicators.push({
      key: "gust_strong",
      severity: "amber",
      label: "Strong gusts",
      detail: `Gusts ${Math.round(input.windGust)} km/h. Risk of being blown offshore. Stay close to shore.`,
    });
  }

  if (input.effectiveWaveHeight === null) {
    indicators.push({ key: "wave_unavailable", severity: "unavailable", label: "Sea-state data unavailable", detail: "The wind-wave forecast is missing, so effective wave height cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.effectiveWaveHeight > SAFETY_WAVE_RED_M) {
    indicators.push({
      key: "wave_extreme",
      severity: "red",
      label: "Dangerous waves",
      detail: `Wave height ${input.effectiveWaveHeight.toFixed(1)}m. Not safe for recreational SUP.`,
    });
  } else if (input.effectiveWaveHeight > SAFETY_WAVE_AMBER_M) {
    indicators.push({
      key: "wave_large",
      severity: "amber",
      label: "Large waves",
      detail: `Wave height ${input.effectiveWaveHeight.toFixed(1)}m. Experienced SUP surfers only.`,
    });
  }

  if (input.visibility === null) {
    indicators.push({ key: "visibility_unavailable", severity: "unavailable", label: "Visibility data unavailable", detail: "The visibility forecast is missing, so shore and traffic awareness cannot be assessed.", confidence: "unavailable", provenance: "missing-forecast-input" });
  } else if (input.visibility < SAFETY_VIS_RED_M) {
    indicators.push({
      key: "visibility_extreme",
      severity: "red",
      label: "Very poor visibility",
      detail: `Visibility ${Math.round(input.visibility)}m. Cannot see hazards, boats, or shore.`,
    });
  } else if (input.visibility < SAFETY_VIS_AMBER_M) {
    indicators.push({
      key: "visibility_reduced",
      severity: "amber",
      label: "Reduced visibility",
      detail: `Visibility ${Math.round(input.visibility)}m. Stay close to shore and carry a whistle.`,
    });
  }

  if (input.uvIndex > SAFETY_UV_RED) {
    indicators.push({
      key: "uv_very_high",
      severity: "red",
      label: `Very high UV (${Math.round(input.uvIndex)})`,
      detail:
        "Extended exposure on open water with no shade. Wear SPF 50+, hat, and UV-protective clothing. Limit session length.",
    });
  } else if (input.uvIndex > SAFETY_UV_AMBER) {
    indicators.push({
      key: "uv_high",
      severity: "amber",
      label: `High UV (${Math.round(input.uvIndex)})`,
      detail: "Apply sunscreen regularly. Consider a UV-protective top.",
    });
  }

  const wc = input.weatherCode;
  const cape = input.capeJkg ?? 0;
  if (wc !== undefined && THUNDERSTORM_CODES.has(wc)) {
    indicators.push({
      key: "lightning",
      severity: "red",
      label: "Thunderstorm",
      detail:
        "Lightning is extremely dangerous on open water. Do not go out. Seek shelter immediately if already on the water.",
    });
  } else if (cape > SAFETY_CAPE_RED) {
    indicators.push({
      key: "lightning_risk",
      severity: "red",
      label: "Thunderstorm risk",
      detail:
        "High convective energy indicates thunderstorm risk. Monitor conditions closely; do not paddle far from shore.",
    });
  } else if (
    cape > SAFETY_CAPE_AMBER &&
    wc !== undefined &&
    CONVECTIVE_RAIN_CODES.has(wc)
  ) {
    indicators.push({
      key: "lightning_possible",
      severity: "amber",
      label: "Thunderstorms possible",
      detail:
        "Convective rain with moderate instability. Stay aware and paddle close to shore.",
    });
  }

  let overall: SafetySeverity = "green";
  for (const ind of indicators) {
    if (ind.severity !== "unavailable") overall = worstSeverity(overall, ind.severity);
  }

  return { overall, indicators };
}

// ---------------------------------------------------------------------------
// Safety policy v2 (all-sports engine design 2.7 and 6.2; review SUP-1, SUP-3)
// ---------------------------------------------------------------------------
//
// Used only for sports listed in CONDITIONS_SAFETY_POLICY_V2_SPORTS: the engine's
// v2 safety modules (engines/coastal/safety/sup.ts and sup-touring.ts) and the
// named transport when a caller asks for policy v2. computeSupSafety above stays
// the revision 1 policy, byte for byte, for every other path.
//
// Differences from computeSupSafety:
// - SUP-1: a Touring offshore-drift row. Within 60 degrees of dead offshore it is
//   amber from 12 km/h sustained or 20 km/h gusts and red from 20 km/h sustained
//   or 30 km/h gusts (owner decision 7; the 37 km/h gust of the accelerated rollout
//   Phase C preparation was withdrawn after review, see sup-offshore-drift.ts). It
//   is unavailable without beach geometry or wind direction
//   whenever the wind could reach the amber threshold; a wind known to be below
//   it cannot carry anyone offshore, so it clears whatever the geometry.
// - SUP-3: UV is classified on the forecast value itself (Open-Meteo gives a
//   fractional index): amber above 8 ("very high") and red at 11 or more
//   ("extreme"), exactly the approved values, so 8.4 is amber and 10.5 is not
//   red. The label shows the value to one decimal, so it never contradicts the
//   tier. The band up to 8 that revision 1 marked amber from 6 is an advisory,
//   not a safety level, so it never reaches the planner veto, the best-day skip
//   or the safety state. CAPE above 1000 J/kg without a thunderstorm code is
//   amber, not red.
// - Missing inputs are never read as clear (design 2.5): a missing UV index or
//   weather code emits an unavailable row ("UV data unavailable", "Storm forecast
//   unavailable") unless a known hazard in the same dimension already shows. A
//   missing gust takes the sustained speed as its lower bound (gusts are never
//   below it), so a strong sustained wind still flags. A sea state known only
//   from one partition flags when that lower bound already exceeds a threshold.
// - Owner decision O-07: the current is an optional safety input (many coastal
//   spots have no current cell), so a missing current is the neutral "no
//   current" and adds no row; the Touring score reads the current as a modifier,
//   so the hour is partial there instead.
// - Owner decision O-17: the UV and storm rows (supUvAndStormSafetyV2) are one
//   rule for every SUP profile; SUP Surf and Downwind take them with their own
//   key prefix (sup-profile-safety.ts).
// - Gust tiers (accelerated rollout Phase B, 1 October 2026): SUP_GUST_V2,
//   amber above 40 km/h and red above 55 km/h (revision 1: 30 and 40), with a
//   sustained-wind row (SUP_SUSTAINED_V2: amber above 30, red above 40 km/h)
//   so a low or missing gust never hides strong wind, and a missing gust
//   always reported unavailable.
// - Current (accelerated rollout Phase C preparation, seaward-current.ts): amber
//   above 0.5 m/s in any direction; red when the seaward component is above
//   1.0 m/s or, at a beach break, the speed is above 2.0 m/s whichever way it
//   sets; at reef, point and river-mouth breaks (and a spot with no recorded
//   type) the speed alone above 1.0 m/s, as revision 1 read it. Open-Meteo's
//   current cell lies offshore and carries the tidal stream along the coast,
//   which at a beach carries a paddler along it to another landing; only the
//   seaward part opposes the paddle back (H5: 23 of 51 red days were such
//   streams at beach breaks). A caller that supplies no seaward component counts
//   the whole speed as seaward, and no relief unless it asks for one. Where the
//   relief, not the speed, keeps the row amber (a stream along the beach above
//   1.0 m/s, faster than a paddler's cruising speed), the row is its own key,
//   current_along_coast, "Strong current along the coast", and tells the
//   paddler they may not paddle back against it; "Moderate current" stays for
//   speeds up to 1.0 m/s (after review, 1 October 2026). Touring paddles out
//   along the coast and back, so that wording is the main signal it gets.

// SUP-1 (the offshore-drift sector, the Touring thresholds and the assessment) lives in
// sup-offshore-drift.ts, the dependency-free module the coastal page briefing also imports
// (P2C-WP9-04), and is re-exported here for the engines and the named transport.
export {
  SUP_OFFSHORE_DRIFT_SECTOR_DEG,
  SUP_TOURING_OFFSHORE_DRIFT_THRESHOLDS,
  assessSupOffshoreDrift,
  supAngleFromOffshoreDeg,
  type SupOffshoreDriftAssessment,
  type SupOffshoreDriftInput,
  type SupOffshoreDriftThresholds,
} from "./sup-offshore-drift";

function finiteNonNegativeOrNull(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

function bearingOrNull(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? ((value % 360) + 360) % 360 : null;
}

/** SUP-3: UV tiers (owner decision 8), applied to the forecast value itself: amber above 8, red at 11 or more. */
export const SUP_UV_V2 = Object.freeze({ amberAbove: 8, redFrom: 11 });

/**
 * A UV index for a policy v2 label: one decimal with a trailing ".0" dropped (8.4 as "8.4", 9 as
 * "9"). An amber value is shown inside its tier (8.1 to 10.9), so a label never names a value of
 * another tier: 8.04 reads "8.1" rather than "8", and 10.97 "10.9" rather than "11". A red value
 * (11 or more) never rounds below 11.
 */
export function formatSupUvIndex(uv: number): string {
  const shown = Math.round(uv * 10) / 10;
  const inTier = uv >= SUP_UV_V2.redFrom
    ? shown
    : uv > SUP_UV_V2.amberAbove
      ? Math.min(SUP_UV_V2.redFrom - 0.1, Math.max(SUP_UV_V2.amberAbove + 0.1, shown))
      : shown;
  return String(Math.round(inTier * 10) / 10);
}
/**
 * Touring (and legacy SUP) gust tiers under policy v2 (accelerated rollout Phase B), in km/h on
 * the forecast's 10 m gust. Revision 1 read amber above 30 and red above 40, but Open-Meteo's gust
 * is the maximum of the hour over its default land cell (review WFD-4) and runs about 1.8 times
 * the sustained wind (H5): the daily daylight maximum gust has a median of 39.2 km/h, so red fired
 * on about half of all days and amber on three quarters, on days of a 12 kn breeze. Offshore wind,
 * the hazard behind about half of paddleboard lifeboat launches, has its own row (SUP-1: red from
 * 20 km/h sustained or 30 km/h gusts within 60 degrees of offshore), so this direction-blind row
 * is about knockdown and losing the board in any direction:
 * - red above 55 km/h (about 30 kn): near-gale gusts knock a standing paddler down and tear the
 *   board away, whatever the direction;
 * - amber above 40 km/h (about 22 kn): strong gusts, kneel in them and stay close to shore.
 */
export const SUP_GUST_V2 = Object.freeze({ amberAboveKmh: 40, redAboveKmh: 55 });
/**
 * Touring (and legacy SUP) sustained-wind tiers under policy v2, in km/h on the forecast's hourly
 * 10 m wind (added after review on 1 October 2026). SUP_GUST_V2 was raised to offset the land-cell
 * gust running about 1.8 times the sustained wind, so the gust tiers alone under-read wind strength
 * wherever that ratio is low (H5 per-spot medians: dakhla 1.25, easkey 1.35, hossegor 1.38, where
 * red would need about 22 to 24 kn sustained) and whenever the gust is missing (a missing gust
 * used to take the sustained speed against the inflated gust tiers, so red needed 30 kn
 * sustained). The sustained wind carries no gust inflation, so it takes revision 1's tiers:
 * - red above 40 km/h (about 22 kn, Beaufort 6): beyond recreational paddling in any direction;
 * - amber above 30 km/h (about 16 kn, Beaufort 4 to 5): experienced paddlers only, close to shore.
 * At the median gust ratio the gust row already reads at least as high, so this row decides only
 * where the gust is low or missing.
 */
export const SUP_SUSTAINED_V2 = Object.freeze({ amberAboveKmh: 30, redAboveKmh: 40 });
/** SUP-3: CAPE above this without a thunderstorm code is amber (revision 1: red). */
export const SUP_CAPE_ONLY_AMBER_JKG = SAFETY_CAPE_RED;

/**
 * The label of an amber current that only the along-coast relief keeps below red: a stream along a
 * beach faster than the profile's red speed (after review, 1 October 2026). Touring and the legacy
 * SUP use it here (key current_along_coast), SUP Surf in sup-profile-safety.ts (key
 * sup-surf-current-along-coast).
 */
export const SUP_ALONG_COAST_CURRENT_LABEL = "Strong current along the coast";

/** The detail of that row for a current of `currentMs` m/s. */
export function supAlongCoastCurrentDetail(currentMs: number): string {
  return `Current ${currentMs.toFixed(1)} m/s, faster than a paddler's cruising speed. You may not be able to paddle back against it; plan a downstream landing or go at slack water.`;
}

/** A policy v2 row: as SafetyIndicator, and the offshore row may name missing geometry. */
export type SupSafetyV2Indicator = Omit<SafetyIndicator, "provenance"> & {
  provenance?: "missing-forecast-input" | "missing-local-geometry";
};

export type SupSafetyV2Result = {
  overall: SafetySeverity;
  indicators: SupSafetyV2Indicator[];
};

export type SupSafetyV2Input = {
  currentVelocity: number | null;
  /**
   * The current's seaward component in m/s (floored at 0). Null or omitted when unresolved: the
   * whole speed then counts as seaward.
   */
  currentSeawardMs?: number | null;
  /**
   * Whether a current along the coast is relieved up to 2.0 m/s: true only at a beach break
   * (seaward-current.ts beachBreakTakesAlongCoastRelief). Omitted: no relief, so a new caller
   * never relaxes a red.
   */
  currentAlongCoastRelief?: boolean;
  windSpeed: number | null;
  windGust: number | null;
  windDirectionDeg: number | null;
  beachFacingDeg: number | null;
  /** The sheltered sea when it is known exactly, else null. */
  effectiveWaveHeight: number | null;
  /** A lower bound of the sheltered sea from the one partition that is known, else null. */
  effectiveWaveHeightMin?: number | null;
  visibility: number | null;
  /**
   * The hour's relative humidity in % (model-fog.ts), the visibility row's saturation test
   * (accelerated rollout Phase E). Null or omitted when unknown: the air counts as saturated, so a
   * missing humidity never lowers the row.
   */
  relativeHumidityPct?: number | null;
  uvIndex: number | null;
  weatherCode: number | null;
  capeJkg: number | null;
};

function unavailableIndicator(
  key: string,
  label: string,
  detail: string,
  provenance: NonNullable<SupSafetyV2Indicator["provenance"]> = "missing-forecast-input",
): SupSafetyV2Indicator {
  return { key, severity: "unavailable", label, detail, confidence: "unavailable", provenance };
}

export type SupUvAndStormInput = Readonly<{ uvIndex: number | null; weatherCode: number | null; capeJkg: number | null }>;

/**
 * SUP-3 under policy v2, one rule for every SUP profile (owner decisions 8 and O-17), in this
 * order: the UV row (amber above 8, red at 11 or more on the forecast value; up to 8 an advisory
 * with no row, so it never vetoes; a missing index "UV data unavailable") and at most one storm row
 * (a thunderstorm code red; CAPE above 1000 J/kg without one amber; CAPE above 500 J/kg with
 * convective rain amber; a missing weather code "Storm forecast unavailable" unless CAPE already
 * shows amber). Keys are Touring's; SUP Surf and Downwind prefix and rename them
 * (sup-profile-safety.ts).
 */
export function supUvAndStormSafetyV2(input: SupUvAndStormInput): SupSafetyV2Indicator[] {
  const indicators: SupSafetyV2Indicator[] = [];
  const uv = finiteNonNegativeOrNull(input.uvIndex);
  const code = typeof input.weatherCode === "number" && Number.isInteger(input.weatherCode) &&
      input.weatherCode >= 0 && input.weatherCode <= 99
    ? input.weatherCode
    : null;
  const cape = finiteNonNegativeOrNull(input.capeJkg);

  if (uv === null) {
    indicators.push(unavailableIndicator("uv_unavailable", "UV data unavailable", "The UV forecast is missing, so sun exposure cannot be assessed."));
  } else if (uv >= SUP_UV_V2.redFrom) {
    indicators.push({ key: "uv_extreme", severity: "red", label: `Extreme UV (${formatSupUvIndex(uv)})`, detail: "Extreme UV on open water with no shade. Avoid the middle of the day, wear SPF 50+, a hat and UV-protective clothing, and keep sessions short." });
  } else if (uv > SUP_UV_V2.amberAbove) {
    indicators.push({ key: "uv_very_high", severity: "amber", label: `Very high UV (${formatSupUvIndex(uv)})`, detail: "Very high UV on open water. Wear SPF 50+, a hat and UV-protective clothing, and limit session length." });
  }

  if (code !== null && THUNDERSTORM_CODES.has(code)) {
    indicators.push({ key: "lightning", severity: "red", label: "Thunderstorm", detail: "Lightning is extremely dangerous on open water. Do not go out. Seek shelter immediately if already on the water." });
  } else if (cape !== null && cape > SUP_CAPE_ONLY_AMBER_JKG) {
    indicators.push({ key: "lightning_risk", severity: "amber", label: "Thunderstorm risk", detail: "High convective energy indicates thunderstorm risk. Monitor conditions closely; do not paddle far from shore." });
  } else if (code !== null && cape !== null && cape > SAFETY_CAPE_AMBER && CONVECTIVE_RAIN_CODES.has(code)) {
    indicators.push({ key: "lightning_possible", severity: "amber", label: "Thunderstorms possible", detail: "Convective rain with moderate instability. Stay aware and paddle close to shore." });
  } else if (code === null) {
    indicators.push(unavailableIndicator("storm_forecast", "Storm forecast unavailable", "The weather code is missing, so thunderstorm risk cannot be assessed."));
  }
  return indicators;
}

/** Touring (and legacy SUP) per-hour safety under policy v2; see the section header. */
export function computeSupSafetyV2(input: SupSafetyV2Input): SupSafetyV2Result {
  const indicators: SupSafetyV2Indicator[] = [];
  const current = finiteNonNegativeOrNull(input.currentVelocity);
  const wind = finiteNonNegativeOrNull(input.windSpeed);
  const rawGust = finiteNonNegativeOrNull(input.windGust);
  const wave = finiteNonNegativeOrNull(input.effectiveWaveHeight);
  const waveMin = finiteNonNegativeOrNull(input.effectiveWaveHeightMin ?? null);
  const visibility = finiteNonNegativeOrNull(input.visibility);

  // O-07: a missing current is the neutral "no current" (no row). A known one is judged by
  // direction at beach breaks (seaward-current.ts; see the section header).
  if (current !== null) {
    const judged = classifySuppliedCurrent(current, input.currentSeawardMs, {
      amberAboveMs: SAFETY_CURRENT_AMBER_MS,
      redSeawardAboveMs: SAFETY_CURRENT_RED_MS,
      alongCoastRelief: input.currentAlongCoastRelief === true,
    });
    if (judged.level === "red") {
      indicators.push({ key: "current_extreme", severity: "red", label: "Strong current", detail: judged.reason === "seaward" && judged.resolved
        ? `Current ${current.toFixed(1)} m/s, ${judged.seawardMs.toFixed(1)} m/s of it setting offshore. Risk of being swept offshore.`
        : `Current ${current.toFixed(1)} m/s exceeds SUP cruising speed. Risk of being swept offshore.` });
    } else if (judged.level === "amber" && current > SAFETY_CURRENT_RED_MS) {
      // Only the along-coast relief keeps it amber: say what it is (see the section header).
      indicators.push({ key: "current_along_coast", severity: "amber", label: SUP_ALONG_COAST_CURRENT_LABEL, detail: supAlongCoastCurrentDetail(current) });
    } else if (judged.level === "amber") {
      indicators.push({ key: "current_moderate", severity: "amber", label: "Moderate current", detail: `Current ${current.toFixed(1)} m/s. Be aware of drift and plan return route.` });
    }
  }

  // Gusts are never below the sustained wind. A missing gust is reported unavailable whatever the
  // other rows read (it is never judged from the sustained wind against the gust tiers, which are
  // set for the inflated land-cell gust; the sustained row below judges that wind).
  const gust = rawGust === null ? null : Math.max(rawGust, wind ?? 0);
  if (gust === null) {
    indicators.push(unavailableIndicator("gust_unavailable", "Gust data unavailable", "The gust forecast is missing, so knockdown risk cannot be assessed."));
  } else if (gust > SUP_GUST_V2.redAboveKmh) {
    indicators.push({ key: "gust_extreme", severity: "red", label: "Dangerous gusts", detail: `Gusts of ${Math.round(gust)} km/h. Near-gale gusts can knock a paddler down and tear the board away.` });
  } else if (gust > SUP_GUST_V2.amberAboveKmh) {
    indicators.push({ key: "gust_strong", severity: "amber", label: "Strong gusts", detail: `Gusts of ${Math.round(gust)} km/h. Kneel in the gusts and stay close to shore.` });
  }

  // Sustained wind (SUP_SUSTAINED_V2): judged on its own, so a low or missing gust never hides it.
  // After the gust row, in the named transport's dimension order (sup-profile-transport.ts).
  // A missing sustained wind is unavailable unless a known gust already rules out amber (the wind
  // is never above its gust).
  if (wind === null) {
    if (rawGust === null || rawGust > SUP_SUSTAINED_V2.amberAboveKmh) {
      indicators.push(unavailableIndicator("wind_unavailable", "Wind data unavailable", "The wind forecast is missing, so wind strength cannot be assessed."));
    }
  } else if (wind > SUP_SUSTAINED_V2.redAboveKmh) {
    indicators.push({ key: "wind_extreme", severity: "red", label: "Very strong wind", detail: `Wind ${Math.round(wind)} km/h. Beyond recreational paddling in any direction; stay off the water.` });
  } else if (wind > SUP_SUSTAINED_V2.amberAboveKmh) {
    indicators.push({ key: "wind_strong", severity: "amber", label: "Strong wind", detail: `Wind ${Math.round(wind)} km/h. Experienced paddlers only; stay close to shore and kneel when it gusts.` });
  }

  const seaLow = wave ?? waveMin;
  if (seaLow !== null && seaLow > SAFETY_WAVE_RED_M) {
    indicators.push({ key: "wave_extreme", severity: "red", label: "Dangerous waves", detail: `Wave height ${seaLow.toFixed(1)}m. Not safe for recreational SUP.` });
  } else if (seaLow !== null && seaLow > SAFETY_WAVE_AMBER_M) {
    indicators.push({ key: "wave_large", severity: "amber", label: "Large waves", detail: `Wave height ${seaLow.toFixed(1)}m. Experienced SUP surfers only.` });
  } else if (wave === null) {
    indicators.push(unavailableIndicator("wave_unavailable", "Sea-state data unavailable", "The wind-wave forecast is missing, so effective wave height cannot be assessed."));
  }

  // Model fog (model-fog.ts, accelerated rollout Phase E): red only in saturated air.
  const fog = classifyModelFog({ visibilityM: visibility, relativeHumidityPct: input.relativeHumidityPct }, SAFETY_VIS_RED_M);
  if (fog === "unavailable") {
    indicators.push(unavailableIndicator("visibility_unavailable", "Visibility data unavailable", "The visibility forecast is missing, so shore and traffic awareness cannot be assessed."));
  } else if (fog === "red") {
    indicators.push({ key: "visibility_extreme", severity: "red", label: "Very poor visibility", detail: `Visibility ${Math.round(visibility as number)}m. Cannot see hazards, boats, or shore.` });
  } else if (fog === "amber") {
    indicators.push({ key: "visibility_reduced", severity: "amber", label: "Reduced visibility", detail: `Visibility ${Math.round(visibility as number)}m. Stay close to shore and carry a whistle.` });
  }

  indicators.push(...supUvAndStormSafetyV2({ uvIndex: input.uvIndex, weatherCode: input.weatherCode, capeJkg: input.capeJkg }));

  const drift = assessSupOffshoreDrift({
    windSpeedKmh: wind,
    windGustKmh: rawGust,
    windDirectionDeg: input.windDirectionDeg,
    beachFacingDeg: input.beachFacingDeg,
  });
  if (drift.level === "red") {
    indicators.push({ key: "offshore_drift", severity: "red", label: "Dangerous offshore wind", detail: "Offshore wind can carry a paddler away from shore." });
  } else if (drift.level === "amber") {
    indicators.push({ key: "offshore_drift", severity: "amber", label: "Offshore wind", detail: "Offshore wind can make the return to shore difficult." });
  } else if (drift.level === "unavailable") {
    indicators.push(unavailableIndicator(
      "offshore_drift",
      "Offshore drift assessment unavailable",
      drift.provenance === "missing-local-geometry"
        ? "Beach geometry is required to assess offshore drift."
        : "Wind direction and complete wind evidence are required to assess offshore drift.",
      drift.provenance,
    ));
  }

  let overall: SafetySeverity = "green";
  for (const indicator of indicators) {
    if (indicator.severity !== "unavailable") overall = worstSeverity(overall, indicator.severity);
  }
  return { overall, indicators };
}

// ---------------------------------------------------------------------------
// Hourly directional shelter for policy v2 and revision 2 (review SUP-4; design 2.5 and 2.7)
// ---------------------------------------------------------------------------
//
// One rule for every path that applies the shared hourly shelter outside revision 1: the SUP
// revision 2 kernels and v2 safety modules (engines/coastal/r2/sup-family.ts supHourShelter) and
// the named transport's policy v2 safety (sup-profile-transport.ts). Revision 1 keeps its own
// getShelterFactor calls, byte for byte. The geometry is validated as the named transport
// validates it (canonicalTouringConfig): a ceiling or floor outside 0 to 1, a ceiling below the
// floor, or a malformed sector list is dropped (the level's default range, or the facing's
// half-plane, then applies), so a bad catalogue row can never amplify the sea. A missing swell
// direction takes the shelter averaged over every direction on a 15-degree grid (the design 2.5
// directional neutral, the same grid as engines/core/missing-data.ts directionAveragedFactor),
// never the base factor alone.

/** Raw shelter geometry as a catalogue row or a config carries it (unvalidated). */
export type SupShelterGeometryInput = Readonly<{
  shelterLevel?: string | null;
  breakType?: string | null;
  exposedSectors?: readonly (readonly number[])[] | null;
  beachFacingDeg?: number | null;
  exposedCeiling?: number | null;
  shelteredFloor?: number | null;
}>;

export type SupShelterGeometry = Readonly<{
  shelterLevel: ShelterLevel | null;
  breakType: string | null;
  exposedSectors: readonly (readonly [number, number])[] | null;
  beachFacingDeg: number | null;
  exposedCeiling: number | null;
  shelteredFloor: number | null;
  /** Supplied fields dropped as invalid, sorted (the engine reports them as a structural neutral). */
  invalidFields: readonly string[];
}>;

/** The direction grid of the shelter's directional neutral (degrees). */
export const SUP_SHELTER_DIRECTION_STEP_DEG = 15;

const SUP_SHELTER_LEVELS: ReadonlySet<string> = new Set<ShelterLevel>(["exposed", "semi_exposed", "semi_sheltered", "sheltered", "inland"]);

function unitIntervalOrNull(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1 ? value : null;
}

function supShelterSectors(value: readonly (readonly number[])[] | null | undefined): readonly (readonly [number, number])[] | null | "invalid" {
  if (value === null || value === undefined) return null;
  if (!Array.isArray(value)) return "invalid";
  if (value.length === 0) return null;
  const sectors: (readonly [number, number])[] = [];
  for (const sector of value) {
    if (!Array.isArray(sector) || sector.length !== 2) return "invalid";
    const start = bearingOrNull(sector[0]);
    const end = bearingOrNull(sector[1]);
    if (start === null || end === null) return "invalid";
    sectors.push(Object.freeze([start, end] as const));
  }
  return Object.freeze(sectors);
}

/** The validated shelter geometry of a location (see the section header). */
export function canonicalSupShelterGeometry(raw: SupShelterGeometryInput): SupShelterGeometry {
  const invalid: string[] = [];
  const level = typeof raw.shelterLevel === "string" && SUP_SHELTER_LEVELS.has(raw.shelterLevel) ? raw.shelterLevel as ShelterLevel : null;
  if (raw.shelterLevel !== null && raw.shelterLevel !== undefined && level === null) invalid.push("shelterLevel");
  const sectors = supShelterSectors(raw.exposedSectors);
  if (sectors === "invalid") invalid.push("exposedSectors");
  const facing = bearingOrNull(raw.beachFacingDeg);
  if (raw.beachFacingDeg !== null && raw.beachFacingDeg !== undefined && facing === null) invalid.push("beachFacingDeg");
  let ceiling = unitIntervalOrNull(raw.exposedCeiling);
  let floor = unitIntervalOrNull(raw.shelteredFloor);
  if (raw.exposedCeiling !== null && raw.exposedCeiling !== undefined && ceiling === null) invalid.push("exposedCeiling");
  if (raw.shelteredFloor !== null && raw.shelteredFloor !== undefined && floor === null) invalid.push("shelteredFloor");
  if (ceiling !== null && floor !== null && ceiling < floor) {
    ceiling = null;
    floor = null;
    invalid.push("exposedCeiling", "shelteredFloor");
  }
  return Object.freeze({
    shelterLevel: level,
    breakType: typeof raw.breakType === "string" ? raw.breakType : null,
    exposedSectors: sectors === "invalid" ? null : sectors,
    beachFacingDeg: facing,
    exposedCeiling: ceiling,
    shelteredFloor: floor,
    invalidFields: Object.freeze([...new Set(invalid)].sort()),
  });
}

/**
 * The hour's shelter factor under the shared rule: getShelterFactor on the validated geometry at
 * the swell direction, or its average over every direction when the swell direction is missing.
 */
export function supHourlyShelterFactor(geometry: SupShelterGeometry, swellDirectionDeg: number | null): number {
  const sectors = geometry.exposedSectors === null ? null : geometry.exposedSectors.map(([start, end]): [number, number] => [start, end]);
  const at = (direction: number) => getShelterFactor(geometry.shelterLevel, geometry.breakType, null, {
    swellDirectionDeg: direction,
    exposedSectors: sectors,
    beachFacingDeg: geometry.beachFacingDeg,
    exposedCeiling: geometry.exposedCeiling,
    shelteredFloor: geometry.shelteredFloor,
  });
  const direction = bearingOrNull(swellDirectionDeg);
  if (direction !== null) return at(direction);
  let sum = 0;
  let count = 0;
  for (let grid = 0; grid < 360; grid += SUP_SHELTER_DIRECTION_STEP_DEG) {
    sum += at(grid);
    count++;
  }
  return sum / count;
}

// ---------------------------------------------------------------------------
// Main hourly scoring function
// ---------------------------------------------------------------------------

export function calculateSupTouringHourly(
  input: SupHourlyInput,
  debug?: boolean
): SupHourlyResult {
  const shelter = getShelterFactor(
    input.shelterLevel,
    input.breakType,
    input.difficulty,
    {
      swellDirectionDeg: input.swellDirectionDeg ?? null,
      exposedSectors: input.exposedSectors ?? null,
      beachFacingDeg: input.beachFacingDeg ?? null,
      exposedCeiling: input.exposedCeiling,
      shelteredFloor: input.shelteredFloor,
    }
  );
  const ewh = input.significantWaveHeight * shelter;

  const factors: SupFactors = {
    wind: supWindFactor(input),
    wave: supWaveFactor(ewh),
    current: supCurrentFactor(input.currentVelocity),
    comfort: supComfortFactor(input),
    visibility: supVisibilityFactor(input),
    tide: supTideFactor(input),
    waterQuality: supWaterQualityFactor(input.secchiDepth),
    golden: supGoldenBonus(input, ewh),
  };

  const rawScore = BASE_SCORE * composeScore(SUP_CHAIN, {
    wind: factors.wind,
    wave: factors.wave,
    current: factors.current,
    comfort: factors.comfort,
    visibility: factors.visibility,
    tide: factors.tide,
    waterQuality: factors.waterQuality,
    golden: factors.golden,
  }).compressed;

  const score = clampScore(rawScore);

  const safety = computeSupSafety({
    currentVelocity: input.currentVelocity,
    windGust: input.windGust,
    effectiveWaveHeight: ewh,
    visibility: input.visibility,
    uvIndex: input.uvIndex,
    weatherCode: input.weatherCode,
    capeJkg: input.capeJkg,
  });

  // TODO: remove after scoring audit is complete
  if (debug) {
    const ew = effectiveWind(input);
    console.log(
      `[SUP-scoring-audit] hour=${input.hour} ` +
        `effWind=${ew.toFixed(1)} effWave=${ewh.toFixed(2)} shelter=${shelter} ` +
        `F_wind=${factors.wind.toFixed(3)} F_wave=${factors.wave.toFixed(3)} ` +
        `F_current=${factors.current.toFixed(3)} F_comfort=${factors.comfort.toFixed(3)} ` +
        `F_vis=${factors.visibility.toFixed(3)} F_tide=${factors.tide.toFixed(3)} ` +
        `F_wq=${factors.waterQuality.toFixed(3)} B_golden=${factors.golden.toFixed(3)} ` +
        `raw=${rawScore.toFixed(2)} score=${score} safety=${safety.overall}`
    );
  }

  const nonGreenIndicators = safety.indicators.filter(
    (i) => i.severity !== "green"
  );

  return {
    score,
    label: supTouringLabel(score),
    factors,
    safety,
    effectiveWaveHeight: ewh,
    safetyCapped: nonGreenIndicators.length > 0,
    safetyCapReasons: nonGreenIndicators.map((i) => i.key),
  };
}

export function calculateSupHourly(
  input: SupHourlyInput,
  debug?: boolean
): SupHourlyResult {
  return calculateSupTouringHourly(input, debug);
}

// ---------------------------------------------------------------------------
// Daily rollup - 30% daylight average + 70% best 3-hour window
// ---------------------------------------------------------------------------

export function deriveSupTouringDaily(
  hourlyScores: { hour: number; score: number; isDark: boolean }[],
): { overall: number; bestWindowStart: number | null; bestWindowScores: number[] } {
  const daily = summariseDailyQuality(
    hourlyScores.map((point) => ({ ...point, isDay: !point.isDark })),
    BEST_WINDOW_HOURS, DAILY_BEST_WINDOW_WEIGHT,
  );
  return {
    overall: Math.round((daily.overall) * 10) / 10,
    bestWindowStart: daily.bestWindowStart,
    bestWindowScores: daily.bestWindowScores,
  };
}

export function deriveSupDaily(
  hourlyScores: { hour: number; score: number; isDark: boolean }[]
): { overall: number; bestWindowStart: number | null } {
  return deriveSupTouringDaily(hourlyScores);
}

// ---------------------------------------------------------------------------
// Label
// ---------------------------------------------------------------------------

export function supTouringLabel(score: number): string {
  if (score >= 9.0) return "Hero";
  if (score >= 7.5) return "Firing";
  if (score >= 6.5) return "Great";
  if (score >= 5.0) return "Good";
  if (score >= 3.5) return "Fair";
  if (score >= 2.0) return "Poor";
  return "Flat";
}

export function supLabel(score: number): string {
  return supTouringLabel(score);
}

// ---------------------------------------------------------------------------
// Re-exports for downstream consumers
// ---------------------------------------------------------------------------

export { isDark };
export type { SunTimes };
